import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  useToast,
  Grid,
  HStack,
  Icon,
  Divider,
  useColorModeValue,
  Select,
  InputGroup,
  InputLeftElement,
  Alert,
  AlertIcon,
  Stack,
  Image,
  Progress,
} from "@chakra-ui/react";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import {
  FiCreditCard,
  FiLock,
  FiShield,
  FiCheck,
  FiUser,
  FiMapPin,
  FiPhone,
} from "react-icons/fi";
import jazzcashCard from "../images/paymentlogo/jazzcash-card.png";
import jazzcashMobile from "../images/paymentlogo/jazzcash-mobile.png";
import easypaisa from "../images/paymentlogo/easypaisa.png";

type PaymentMethod = "jazzcash-card" | "jazzcash-mobile" | "easypaisa";

const Checkout: React.FC = () => {
  const { items, total } = useCart();
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>("jazzcash-card");
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const handlePlaceOrder = async () => {
    setIsProcessing(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      // Handle order placement logic here
      navigate("/order-success");
    } catch (error) {
      toast({
        title: "Payment Failed",
        description: "Please try again or choose a different payment method.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Container maxW="1400px" py={8}>
      {/* Progress Indicator */}
      <VStack spacing={8} align="stretch">
        <Box>
          <Progress value={100} colorScheme="green" size="sm" mb={2} />
          <Text fontSize="sm" color="gray.500">Step 2 of 2: Review & Payment</Text>
        </Box>

        <Grid templateColumns={["1fr", "1fr 1fr"]} gap={8}>
          {/* Left Section - Customer Information and Payment */}
          <VStack spacing={6} align="stretch">
            <Box
              as="form"
              onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                handlePlaceOrder();
              }}
              bg={bgColor}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={6}
              shadow="md"
            >
              <VStack spacing={6} align="stretch">
                <Heading size="lg">Customer Information</Heading>

                <FormControl isRequired>
                  <FormLabel>Full Name</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiUser} color="gray.400" />
                    </InputLeftElement>
                    <Input placeholder="Enter your full name" />
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input type="email" placeholder="Enter your email" />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Phone Number</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiPhone} color="gray.400" />
                    </InputLeftElement>
                    <Input placeholder="Enter your phone number" />
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Delivery Address</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiMapPin} color="gray.400" />
                    </InputLeftElement>
                    <Input placeholder="Enter your delivery address" />
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Country</FormLabel>
                  <Select placeholder="Select country">
                    <option value="pakistan">Pakistan</option>
                    <option value="us">United States</option>
                    <option value="australia">Australia</option>
                    <option value="uk">United Kingdom</option>
                    <option value="canada">Canada</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>City</FormLabel>
                  <Select placeholder="Select city">
                    <option value="karachi">Karachi</option>
                    <option value="lahore">Lahore</option>
                    <option value="islamabad">Islamabad</option>
                    <option value="rawalpindi">Rawalpindi</option>
                    <option value="faisalabad">Faisalabad</option>
                  </Select>
                </FormControl>

                <Divider />

                <Heading size="lg">Payment Method</Heading>

                <VStack spacing={4} align="stretch">
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={
                      paymentMethod === "jazzcash-card" ? "green.500" : borderColor
                    }
                    cursor="pointer"
                    _hover={{ borderColor: "green.500" }}
                    onClick={() => setPaymentMethod("jazzcash-card")}
                    bg={
                      paymentMethod === "jazzcash-card" ? "green.50" : "transparent"
                    }
                  >
                    <Radio
                      value="jazzcash-card"
                      isChecked={paymentMethod === "jazzcash-card"}
                    >
                      <HStack spacing={3}>
                        <Image
                          src={jazzcashCard}
                          alt="JazzCash Debit/Credit Card"
                          height="24px"
                          objectFit="contain"
                        />
                        <Text fontWeight="bold">JazzCash Debit/Credit Card</Text>
                      </HStack>
                    </Radio>
                  </Box>

                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={
                      paymentMethod === "jazzcash-mobile" ? "green.500" : borderColor
                    }
                    cursor="pointer"
                    _hover={{ borderColor: "green.500" }}
                    onClick={() => setPaymentMethod("jazzcash-mobile")}
                    bg={
                      paymentMethod === "jazzcash-mobile"
                        ? "green.50"
                        : "transparent"
                    }
                  >
                    <Radio
                      value="jazzcash-mobile"
                      isChecked={paymentMethod === "jazzcash-mobile"}
                    >
                      <HStack spacing={3}>
                        <Image
                          src={jazzcashMobile}
                          alt="JazzCash Mobile"
                          height="24px"
                          objectFit="contain"
                        />
                        <Text fontWeight="bold">JazzCash Mobile</Text>
                      </HStack>
                    </Radio>
                  </Box>

                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor={
                      paymentMethod === "easypaisa" ? "green.500" : borderColor
                    }
                    cursor="pointer"
                    _hover={{ borderColor: "green.500" }}
                    onClick={() => setPaymentMethod("easypaisa")}
                    bg={paymentMethod === "easypaisa" ? "green.50" : "transparent"}
                  >
                    <Radio
                      value="easypaisa"
                      isChecked={paymentMethod === "easypaisa"}
                    >
                      <HStack spacing={3}>
                        <Image
                          src={easypaisa}
                          alt="Easypaisa"
                          height="24px"
                          objectFit="contain"
                        />
                        <Text fontWeight="bold">Easypaisa</Text>
                      </HStack>
                    </Radio>
                  </Box>
                </VStack>

                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    To change or edit your order, go back. No changes will be
                    allowed once you click "PLACE ORDER".
                  </Text>
                </Alert>

                <Button
                  type="submit"
                  colorScheme="green"
                  size="lg"
                  isLoading={isProcessing}
                  loadingText="Processing Payment"
                  leftIcon={<FiLock />}
                >
                  Place Order
                </Button>
              </VStack>
            </Box>
          </VStack>

          {/* Right Section - Order Summary */}
          <VStack spacing={6} align="stretch">
            <Heading size="lg">Order Summary</Heading>
            
            <Box
              bg={bgColor}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={6}
              shadow="md"
            >
              <VStack spacing={4} align="stretch">
                <Heading size="md">Products</Heading>
                {items.map((item) => (
                  <HStack
                    key={item.id}
                    justify="space-between"
                    align="start"
                    spacing={4}
                  >
                    <HStack spacing={4} align="start">
                      <Box
                        w="60px"
                        h="60px"
                        borderRadius="md"
                        overflow="hidden"
                        flexShrink={0}
                      >
                        <Image
                          src={item.image}
                          alt={item.name}
                          w="100%"
                          h="100%"
                          objectFit="cover"
                        />
                      </Box>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">{item.name}</Text>
                        <Text color="gray.500">Quantity: {item.quantity}</Text>
                      </VStack>
                    </HStack>
                    <Text fontWeight="medium">Rs. {item.price.toFixed(2)}</Text>
                  </HStack>
                ))}
                <Divider />
                <HStack justify="space-between">
                  <Text fontWeight="bold">Total</Text>
                  <Text fontWeight="bold" fontSize="xl">
                    Rs. {total.toFixed(2)}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            <Box
              bg={bgColor}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={6}
              shadow="md"
            >
              <VStack spacing={4} align="stretch">
                <Heading size="md">Secure Payment</Heading>
                <HStack spacing={4}>
                  <Icon as={FiLock} color="green.500" />
                  <Text>SSL Encrypted Payment</Text>
                </HStack>
                <HStack spacing={4}>
                  <Icon as={FiShield} color="green.500" />
                  <Text>Secure Payment Processing</Text>
                </HStack>
                <HStack spacing={4}>
                  <Icon as={FiCheck} color="green.500" />
                  <Text>Money-back Guarantee</Text>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </Grid>
      </VStack>
    </Container>
  );
};

export default Checkout;
